import { ActionTree, GetterTree, Module, MutationTree } from "vuex"
import { RootState } from "@/store/RootStore"
import { make } from "vuex-pathify"
import { Amenity, DraggableGroup, LoyaltyActivity, Promotion, PaginationInfo, Utils } from "rp-components"
import { StoreUtils } from "@/utils/StoreUtils"
import services from "@/services/Services"
import _ from "lodash"

export const namespaced: boolean = true

export interface StoreLinks {
    ios: string
    android: string
}

export interface LoyaltyState {
    amenities?: Amenity[]
    amenityListItems?: DraggableGroup<Amenity>[]
    amenity?: Amenity
    promotion?: Promotion
    originalPromotion?: Promotion
    promotions: Promotion[]
    loyaltyActivities?: LoyaltyActivity[]
    activityPagination: PaginationInfo
    storeLinks?: StoreLinks
}

export const state: LoyaltyState = {
    amenities: undefined,
    amenityListItems: undefined,
    amenity: undefined,
    promotion: undefined,
    originalPromotion: undefined,
    promotions: [],
    loyaltyActivities: undefined,
    activityPagination: {
        pageSize: 10,
        nextLink: undefined
    },
    storeLinks: undefined
}

//===================================
// Getters
//===================================
export const getters: GetterTree<LoyaltyState, RootState> = {}

//===================================
// Actions
//===================================
export const actions: ActionTree<LoyaltyState, RootState> = {
    ...make.actions(state),
    toggleAmenityListItemExpanded({ commit }, index: number) {
        commit("toggleAmenityListItemExpanded", index)
    },

    buildAmenityListItems({ dispatch }) {
        const items = _.chain(state.amenities)
            .cloneDeep()
            .sortBy(["typePriority", "priority"])
            .groupBy("type")
            .map((v, k) => {
                return { title: k, children: v, expanded: true }
            })
            .value()

        dispatch("setAmenityListItems", items)
    },

    async loadTenantAmenities({ dispatch }, propertyId: number) {
        await StoreUtils.withLoading(dispatch, async () => {
            const amenities = await services.loyalty.listAmenities(propertyId)
            dispatch(
                "setAmenities",
                amenities.filter((a) => a.propertyId == propertyId)
            )
            dispatch("buildAmenityListItems")
        })
    },

    async listPromotions({ dispatch }, payload: { propertyId: number; amenityId: string }) {
        await StoreUtils.withLoading(dispatch, async () => {
            const promotions = await services.loyalty.listPromotions(payload.propertyId, payload.amenityId)
            dispatch("setPromotions", promotions)
        })
    },

    async getPromotion({ dispatch }, payload: { propertyId: number; amenityId: string; promotionId: string }) {
        await StoreUtils.withLoading(dispatch, async () => {
            const { propertyId, amenityId, promotionId } = payload
            const promotion = await services.loyalty.getPromotion(propertyId, amenityId, promotionId)
            dispatch("setOriginalPromotion", Utils.clone(promotion)!)
            dispatch("setPromotion", promotion)
        })
    },

    async savePromotion({ dispatch }, promotion: Promotion) {
        await StoreUtils.withLoading(dispatch, async () => {
            const savedPromotion = await services.loyalty.savePromotion(promotion)
            dispatch("setOriginalPromotion", Utils.clone(savedPromotion))
            dispatch("setPromotion", savedPromotion)
        })
    },

    async loadAmenity({ dispatch }, payload: { propertyId: number; amenityId: string }) {
        await StoreUtils.withLoading(dispatch, async () => {
            const amenity = await services.loyalty.getAmenity(payload.propertyId, payload.amenityId)
            dispatch("setAmenity", amenity)
        })
    },

    async getActivityLog({ dispatch }, payload: { propertyId: number; amenityId: string; promotionId: string }) {
        await StoreUtils.withLoading(dispatch, async () => {
            const { propertyId, amenityId, promotionId } = payload
            const activites = await services.loyalty.getPromotionActivities(propertyId, amenityId, promotionId, state.activityPagination.pageSize)
            dispatch("setLoyaltyActivities", Utils.clone(activites.items)!)
        })
    },

    async getNextActivityPage({ dispatch }) {
        await StoreUtils.withLoading(dispatch, async () => {
            const nextUri = state.activityPagination.nextLink
            if (nextUri) {
                const pageSize = state.activityPagination.pageSize
                const activityPage = await services.loyalty.nextActivityPage(nextUri, state.activityPagination.pageSize)
                dispatch("setLoyaltyActivities", [...new Set((state.loyaltyActivities ?? []).concat(activityPage.items))])
                dispatch("setActivityPagination", {
                    pageSize: pageSize,
                    nextLink: activityPage.next
                })
            }
        })
    },

    async reverseLoyaltyPunch({ dispatch }, payload: { propertyId: number; amenityId: string; promotionId: string; loyaltyActivityId: string }) {
        await StoreUtils.withLoading(dispatch, async () => {
            const { propertyId, amenityId, promotionId, loyaltyActivityId } = payload
            await services.loyalty.reverseLoyaltyPunch(propertyId, amenityId, promotionId, loyaltyActivityId)

            const activites = await services.loyalty.getPromotionActivities(propertyId, amenityId, promotionId, state.activityPagination.pageSize)
            dispatch("setLoyaltyActivities", Utils.clone(activites)!)
            dispatch("setAlert", "The loyalty transaction was successfully reversed.", { root: true })
        })
    },

    async getStoreLinks({ dispatch }) {
        await StoreUtils.withLoading(
            dispatch,
            async () => {
                const links = await services.loyalty.getStoreLinks()
                dispatch("setStoreLinks", links)
            },
            async () => {
                return true // Suppress default error toast — pages handle their own error UI
            }
        )
    }
}

//===================================
// Mutations
//===================================
export const mutations: MutationTree<LoyaltyState> = {
    ...make.mutations(state),

    toggleAmenityListItemExpanded(state, index) {
        if (state.amenityListItems) {
            state.amenityListItems[index].expanded = !state.amenityListItems[index].expanded
        }
    }
}

//===================================
// Module
//===================================
export const loyalty: Module<LoyaltyState, RootState> = {
    namespaced,
    state: state,
    getters: getters,
    actions: actions,
    mutations: mutations
}
